/**
 * 保存状態の表示（docs/01-requirements.md §5.9）。
 *
 * 規律:
 * - **独立したコンポーネントにしてある。** 親（NotePage）が状態を持つと、
 *   保存ステータスの更新でエディタが再描画されうる。Editor 自体は memo で
 *   隔離されているが、念のためこちら側も最小の描画範囲に閉じる。
 *   **これは見せ方を変えても壊してはいけない**（ADR-036 の「残る制約」）。
 *
 * 見せ方（色・動き・大きさ）に要件上の制約はない（2026-08-01 に緩和。ADR-036）。
 * 自動保存はバックグラウンド処理なので、**離脱するときに読み取れること**を優先し、
 * 状態をチップとして塗り分ける（ADR-037）:
 *
 * - 保存済み = Green（DESIGN-SYSTEM.md §3「成功・完了・正常状態」）＋ チェック
 * - 保存中 / 未送信 = Cream（静かな補足。§3 のとおり境界線と併用する）
 * - エラー = Danger
 *
 * 「保存済み」へ入った瞬間だけ、短い反応を 1 回だけ出す（§2 Small delight が名指しする
 * 「完了時のフィードバック」）。**常時動くものにはしない**（§10 の過剰側）。
 */

import { memo, useEffect, useRef, useState } from 'react';
import { Icon } from '@app/components/icons';
import type { SaveStatus } from '@app/save/note-saver';

const LABEL: Record<SaveStatus, string> = {
  saved: '保存済み',
  saving: '保存中',
  pending: '未送信',
  error: 'エラー（再試行中）',
};

export interface SaveStatusChipProps {
  status: SaveStatus;
  onRetry: () => void;
}

function SaveStatusChipImpl({ status, onRetry }: SaveStatusChipProps) {
  // 「保存済み」へ**入った瞬間**だけ反応させる。初回描画では鳴らさない
  //（画面を開くたびに動くと、§10 の「常時キラキラ」側へ寄る）。
  const previous = useRef<SaveStatus>(status);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    const entered = previous.current !== 'saved' && status === 'saved';
    previous.current = status;
    if (!entered) return;
    setJustSaved(true);
    const timer = setTimeout(() => setJustSaved(false), 600);
    return () => clearTimeout(timer);
  }, [status]);

  const label = (
    <span
      className={
        `save-status__label save-status__label--${status}` +
        (justSaved ? ' save-status__label--just-saved' : '')
      }
    >
      {status === 'saved' && <Icon name="check" size={14} />}
      {LABEL[status]}
    </span>
  );

  return (
    <span className="save-status" role="status" aria-live="polite">
      {status === 'error' ? (
        // **チップ自体を再試行ボタンにする。** 別に「今すぐ再試行」のリンクを並べると、
        // ヘッダの残り幅を超えて画面名が潰れ、リンク側は 44px のタップ領域も取れない。
        // 押せることは枠線（他の状態は枠を持たない）と `title` が示す。
        // aria-label に状態も含めるのは、この要素が live region の中身そのものだから。
        <button
          type="button"
          className="save-status__retry"
          onClick={onRetry}
          aria-label={`${LABEL.error}。押すと今すぐ再試行します`}
          title="今すぐ再試行"
        >
          {label}
        </button>
      ) : (
        label
      )}
    </span>
  );
}

export const SaveStatusChip = memo(SaveStatusChipImpl);
SaveStatusChip.displayName = 'SaveStatusChip';
